import { convertPrice } from '@/utils/common';

/** Các field hiển thị trên modal lịch sử — ưu tiên *Name từ BE */
const DISPLAY_FIELDS = [
  { key: 'code', label: 'Mã phiếu' },
  { key: 'sourceWarehouseName', label: 'Kho', fallbackKey: 'storeName' },
  { key: 'providerName', label: 'Nhà cung cấp' },
  { key: 'subProviderName', label: 'Nhà cung cấp phụ' },
  { key: 'stockSlipDate', label: 'Ngày phiếu' },
  { key: 'description', label: 'Ghi chú' },
  { key: 'purchaseOrderCode', label: 'Mã PO' },
  { key: 'discountAmount', label: 'Chiết khấu' },
  { key: 'discountTypeName', label: 'Loại chiết khấu', fallbackKey: 'discountType' },
  { key: 'vatAmount', label: 'VAT' },
  { key: 'vatTypeName', label: 'Loại VAT', fallbackKey: 'vatType' },
  { key: 'vatCode', label: 'Số hóa đơn VAT' },
  { key: 'vatSerial', label: 'Ký hiệu' },
  { key: 'vatDate', label: 'Ngày xuất VAT' },
  { key: 'totalAmount', label: 'Tổng tiền' },
  { key: 'typeName', label: 'Loại phiếu' },
  { key: 'hasVat', label: 'Có VAT' },
];

const DETAIL_FIELDS = [
  { key: 'quantity', label: 'Số lượng' },
  { key: 'unitPrice', label: 'Đơn giá', format: 'price' },
  { key: 'beforeVatPrice', label: 'ĐG trước VAT', format: 'price' },
  { key: 'discountAmount', label: 'Chiết khấu', format: 'price' },
  { key: 'productImei', label: 'IMEI', format: 'imei', altKey: 'imeis' },
];

function parseSnapshot(value) {
  if (value == null || value === '') return null;
  if (typeof value === 'object') return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function pickFieldValue(snapshot, key, fallbackKey) {
  if (!snapshot) return undefined;
  if (snapshot[key] != null && snapshot[key] !== '') {
    return snapshot[key];
  }
  if (fallbackKey && snapshot[fallbackKey] != null && snapshot[fallbackKey] !== '') {
    return snapshot[fallbackKey];
  }
  return undefined;
}

function formatFieldValue(key, value, snapshot) {
  if (value == null || value === '') return '';

  if (['discountAmount', 'vatAmount', 'totalAmount'].includes(key)) {
    return convertPrice(value);
  }
  if (key === 'hasVat') {
    return value ? 'Có' : 'Không';
  }
  if (key === 'discountType' && !snapshot?.discountTypeName) {
    return value === 2 ? 'Phần trăm' : value === 1 ? 'Tiền mặt' : String(value);
  }
  if (key === 'vatType' && !snapshot?.vatTypeName) {
    return value === 2 ? 'Phần trăm' : value === 1 ? 'Tiền mặt' : String(value);
  }
  return String(value);
}

function normalizeCompareValue(value, format) {
  if (value == null || value === '') return null;
  if (format === 'price') {
    const n = Number(String(value).replace(/[^\d.-]/g, ''));
    return Number.isFinite(n) ? n : String(value);
  }
  if (format === 'imei') {
    return parseImeiString(value).join(',');
  }
  return value;
}

function valuesEqual(a, b) {
  return JSON.stringify(a ?? null) === JSON.stringify(b ?? null);
}

function resolveDisplayValue(snapshot, fieldDef) {
  const { key, fallbackKey } = fieldDef;
  const raw = pickFieldValue(snapshot, key, fallbackKey);
  if (raw === undefined) return '';
  return formatFieldValue(key, raw, snapshot);
}

export function parseImeiString(str) {
  if (!str || !String(str).trim()) return [];
  return String(str)
    .split(',')
    .map((s) => s.trim())
    .filter(Boolean);
}

export function diffImeiCodes(beforeStr, afterStr) {
  const beforeList = parseImeiString(beforeStr);
  const afterList = parseImeiString(afterStr);
  const beforeSet = new Set(beforeList);
  const afterSet = new Set(afterList);
  return {
    before: beforeList,
    after: afterList,
    added: afterList.filter((c) => !beforeSet.has(c)),
    removed: beforeList.filter((c) => !afterSet.has(c)),
    changed: beforeList.some((c) => !afterSet.has(c)) || afterList.some((c) => !beforeSet.has(c)),
  };
}

function pickDetailRaw(detail, fieldDef) {
  const { key, altKey } = fieldDef;
  const raw = detail?.[key];
  if (raw != null && raw !== '') return raw;
  if (altKey && detail?.[altKey] != null && detail[altKey] !== '') {
    return detail[altKey];
  }
  return null;
}

function formatDetailFieldDisplay(raw, format) {
  if (raw == null || raw === '') return '';
  if (format === 'price') return convertPrice(raw);
  if (format === 'imei') return parseImeiString(raw);
  return String(raw);
}

function buildDetailFieldRows(beforeDetail, afterDetail) {
  const fields = [];

  DETAIL_FIELDS.forEach((fieldDef) => {
    const beforeRaw = pickDetailRaw(beforeDetail, fieldDef);
    const afterRaw = pickDetailRaw(afterDetail, fieldDef);
    const beforeNorm = normalizeCompareValue(beforeRaw, fieldDef.format);
    const afterNorm = normalizeCompareValue(afterRaw, fieldDef.format);
    const changed = !valuesEqual(beforeNorm, afterNorm);

    if (fieldDef.format === 'imei') {
      const imeiDiff = diffImeiCodes(beforeRaw, afterRaw);
      fields.push({
        key: fieldDef.key,
        label: fieldDef.label,
        format: 'imei',
        before: imeiDiff.before,
        after: imeiDiff.after,
        imeiDiff,
        changed: imeiDiff.changed,
      });
      return;
    }

    fields.push({
      key: fieldDef.key,
      label: fieldDef.label,
      format: fieldDef.format || 'text',
      before: formatDetailFieldDisplay(beforeRaw, fieldDef.format),
      after: formatDetailFieldDisplay(afterRaw, fieldDef.format),
      changed,
    });
  });

  return fields;
}

export function buildStockSlipHeaderDiffRows(originalValue, newValue) {
  const original = parseSnapshot(originalValue) || {};
  const current = parseSnapshot(newValue) || {};
  const rows = [];

  DISPLAY_FIELDS.forEach((fieldDef) => {
    const before = resolveDisplayValue(original, fieldDef);
    const after = resolveDisplayValue(current, fieldDef);
    if (valuesEqual(before, after)) return;
    if (!before && !after) return;

    rows.push({
      key: fieldDef.key,
      label: fieldDef.label,
      before: before || '—',
      after: after || '—',
      changed: true,
    });
  });

  return rows;
}

function detailLineLabel(detail) {
  if (!detail) return '';
  const parts = [detail.productCode, detail.productName, detail.barCode].filter(Boolean);
  return parts.join(' - ') || `SP #${detail.productId || ''}`;
}

function detailMapKey(item, index) {
  return String(item?.id ?? item?.productId ?? `idx-${index}`);
}

export function buildStockSlipDetailDiffRows(originalValue, newValue) {
  const original = parseSnapshot(originalValue) || {};
  const current = parseSnapshot(newValue) || {};
  const beforeDetails = Array.isArray(original.details) ? original.details : [];
  const afterDetails = Array.isArray(current.details) ? current.details : [];

  const beforeMap = new Map(beforeDetails.map((item, index) => [detailMapKey(item, index), item]));
  const afterMap = new Map(afterDetails.map((item, index) => [detailMapKey(item, index), item]));
  const keys = new Set([...beforeMap.keys(), ...afterMap.keys()]);
  const rows = [];

  keys.forEach((key) => {
    const before = beforeMap.get(key);
    const after = afterMap.get(key);
    const fields = buildDetailFieldRows(before, after);
    const hasChanges = fields.some((f) => f.changed);

    if (!before && after) {
      rows.push({
        productKey: key,
        label: detailLineLabel(after),
        productCode: after.productCode,
        changeType: 'added',
        fields: fields.filter((f) => f.after && (Array.isArray(f.after) ? f.after.length : true)),
      });
      return;
    }
    if (before && !after) {
      rows.push({
        productKey: key,
        label: detailLineLabel(before),
        productCode: before.productCode,
        changeType: 'removed',
        fields: fields.filter((f) => f.before && (Array.isArray(f.before) ? f.before.length : true)),
      });
      return;
    }
    if (!hasChanges) return;

    rows.push({
      productKey: key,
      label: detailLineLabel(after || before),
      productCode: (after || before)?.productCode,
      changeType: 'modified',
      fields,
    });
  });

  return rows;
}

/** @deprecated — giữ cho tương thích; dùng buildStockSlipDetailDiffRows structured fields */
function formatDetailSnapshot(detail) {
  if (!detail) return '';
  const qty = detail.quantity != null ? detail.quantity : '';
  const price =
    detail.afterVatPrice != null
      ? convertPrice(detail.afterVatPrice)
      : detail.unitPrice != null
        ? convertPrice(detail.unitPrice)
        : '';
  const imei = detail.productImei || detail.imeis || '';
  const parts = [`SL: ${qty}`, price ? `ĐG: ${price}` : null, imei ? `IMEI: ${imei}` : null].filter(
    Boolean,
  );
  return parts.join(' | ');
}

export { formatDetailSnapshot };

export function parseHistorySnapshot(value) {
  return parseSnapshot(value);
}
